import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Address } from '@checkout-v2/const';
import { DeliveryAddressService } from '@checkout-v2/feature';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  AutofocusDirective,
  MaxNumberDirective,
  MinNumberDirective,
  OnlyNumberDirective,
  SdvorSelectModule,
} from '@sdvor/shared/ui';
import {
  SdModalService,
  SdvorButtonComponent,
  SdvorCheckboxComponent,
  SdvorIconComponent,
  SdvorInputComponent,
} from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { FormErrorsComponent } from '@spartacus/storefront';
import { GeocodeResult } from '@yandex-maps/const';
import { AddressSuggestionsService, GeocodeService } from '@yandex-maps/feature';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import {
  concatMap,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  skip,
  take,
} from 'rxjs/operators';
import { DeliveryAddressDialogComponentService } from './delivery-address-dialog.component.service';
import { DeliveryAddressMapComponent } from './delivery-address-map';
import {
  AddressAdditionalData,
  AddressData,
  mapAddressToFormData,
  mapFormDataToAddress,
  Nullable,
} from './delivery-address.mapper';

export type { AddressAdditionalData, AddressData } from './delivery-address.mapper';

type AddressFormControls = {
  [K in keyof Required<AddressData>]: FormControl<AddressData[K] | null>;
};
@Component({
  selector: 'sd-delivery-address-dialog',
  imports: [
    AsyncPipe,
    SdvorSelectModule,
    SdvorIconComponent,
    SdvorCheckboxComponent,
    SdvorInputComponent,
    SdvorButtonComponent,
    ReactiveFormsModule,
    FormErrorsComponent,
    DeliveryAddressMapComponent,
    AutofocusDirective,
    MaxNumberDirective,
    MinNumberDirective,
    OnlyNumberDirective,
  ],
  templateUrl: './delivery-address-dialog.component.html',
  styleUrls: ['./delivery-address-dialog.component.scss'],
  providers: [DeliveryAddressDialogComponentService, AddressSuggestionsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryAddressDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly componentService = inject(DeliveryAddressDialogComponentService);
  protected readonly suggestionsService = inject(AddressSuggestionsService);
  private readonly fb = inject(FormBuilder);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly geocodeService = inject(GeocodeService);
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(SdModalService);

  @ViewChild('ngSelect') ngSelect: NgSelectComponent;

  selectTypeahead = new Subject<string>();
  isTabletMdDown$: Observable<boolean>;
  iconTypes = IconTypes;

  form: FormGroup<AddressFormControls> = this.fb.group<AddressFormControls>({
    address: this.fb.control<string | null>('', this.addressValidator.bind(this)),

    hasLimit: this.fb.control<boolean | null>(null),
    heightLimit: this.fb.control<number | null>(null),
    widthLimit: this.fb.control<number | null>(null),

    privateHouse: this.fb.control<boolean | null>(null),

    appartment: this.fb.control<string | null>(null),
    entrance: this.fb.control<string | null>(null),
    floor: this.fb.control<number | null>(null),
    domofon: this.fb.control<string | null>(null),
    passengerLift: this.fb.control<boolean | null>(null),
    cargoLift: this.fb.control<boolean | null>(null),
    carryingDistance: this.fb.control<number | null>(null),

    // Невидимые поля
    addressAdditionalData: this.fb.control<AddressAdditionalData | null>(null, Validators.required),
  });

  addressData$ = merge(
    this.deliveryAddressService.editableAddressId$.pipe(
      concatMap((addressId) => {
        if (addressId) {
          return this.getExistingAddressData(addressId);
        }

        return of(this.getEmptyAddressData());
      }),
      take(1),
      delay(0),
    ),
    this.componentService.geoObject$.pipe(
      filter((geoObject) => !!geoObject),
      map(({ geocodeResult, exactPoint }) => ({
        ...(<Nullable<AddressData>>this.form.value),
        address: geocodeResult.getAddressLine(),
        addressAdditionalData: {
          country: {
            isocode: geocodeResult.getCountryCode(),
          },
          regionName: geocodeResult.getAdministrativeAreas()[1],
          district: geocodeResult.getArea(),
          town: geocodeResult.getLocalities()[0],
          streetname: geocodeResult.getThoroughfare(),
          streetnumber: geocodeResult.getPremiseNumber(),
          point: exactPoint ?? {
            latitude: geocodeResult.geometry.getCoordinates()?.[0],
            longitude: geocodeResult.geometry.getCoordinates()?.[1],
          },
        },
      })),
    ) as Observable<Nullable<AddressData>>,
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  activeAddressId$ = this.deliveryAddressService.editableAddressId$.asObservable();
  mapIsShown$ = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    this.isTabletMdDown$ = this.deviceService.isTabletMdDown$;

    this.addressData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.form.setValue(data);
      this.applyConditionalValidators();
    });

    this.form.controls.privateHouse.valueChanges
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((isPrivateHouse) => {
        this.toggleApartmentValidators(isPrivateHouse === true);

        if (isPrivateHouse) {
          this.resetApartmentData();
        }
      });

    this.form.controls.hasLimit.valueChanges
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.toggleTransitLimitValidators(value === true);

        if (value === false) {
          this.resetTransitLimits();
        }
      });

    this.form.controls.heightLimit.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.controls.widthLimit.updateValueAndValidity({ emitEvent: false });
      });

    this.form.controls.widthLimit.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.controls.heightLimit.updateValueAndValidity({ emitEvent: false });
      });

    this.deliveryAddressService.savedAddressEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.modalService.closeActiveModal());
  }

  ngAfterViewInit(): void {
    this.selectTypeahead
      .pipe(debounceTime(700), takeUntilDestroyed(this.destroyRef))
      .subscribe(this.suggestionsService.loadSuggestions);

    this.addressData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.ngSelect.searchInput.nativeElement.value = data.address ?? '';
    });

    this.activeAddressId$
      .pipe(
        filter<string>(Boolean),
        concatMap(() => this.addressData$),
        concatMap((data) =>
          this.geocodeService.getGeoObject(data.address ?? '').pipe(
            map((geocodeResult) => {
              const point = data.addressAdditionalData?.point;
              const exactPoint = point?.latitude && point?.longitude ? point : undefined;

              return {
                geocodeResult,
                exactPoint,
              };
            }),
          ),
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ geocodeResult, exactPoint }) => {
        const coordinates: ymaps.Coordinate = exactPoint
          ? [exactPoint.latitude, exactPoint.longitude]
          : geocodeResult.geometry.getCoordinates();

        this.componentService.setGeoObject({ geocodeResult, exactPoint });
        this.componentService.pintMapToCoord(coordinates);
      });
  }

  ngOnDestroy(): void {
    this.deliveryAddressService.editableAddressId$.next(undefined);
  }

  protected toggleMap(isShown: boolean = !this.mapIsShown$.value): void {
    this.mapIsShown$.next(isShown);
  }

  protected closeMap(): void {
    this.toggleMap(false);
  }

  protected onBlurSelect(e: FocusEvent): void {
    const input = e.target as HTMLInputElement;
    this.setAddressValue(input.value);
  }

  protected onChangeSelect(suggestion?: string): void {
    if (!suggestion) return;

    this.form.controls.address.disable();
    this.ngSelect.searchInput.nativeElement.value = suggestion;

    this.geocodeService
      .getGeoObject(suggestion)
      .pipe(
        finalize(() => this.form.controls.address.enable()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.setAddressValue(suggestion);
          this.componentService.setGeoObject({ geocodeResult: data });
          this.componentService.pintMapToCoord(data.geometry.getCoordinates());

          const entrance = this.extractEntrance(data);
          if (entrance && this.form.controls.privateHouse.value !== true) {
            this.form.controls.entrance.setValue(entrance);
          }
        },
        error: () => this.setAddressValue(suggestion),
      });
  }

  protected onSearchSelect({ term }: { term: string }): void {
    // Обновляем form control без writeValue в ng-select.
    // Иначе при совпадении term с bindValue подсказка становится selected,
    // и клик по ней не эмитит onChangeSelect не вызывается.
    this.form.get('address').setValue(term, { emitModelToViewChange: false });
    this.ngSelect?.writeValue(null);
  }

  protected submitAddressValue(): void {
    this.form.markAllAsTouched();

    if (!this.form.valid || !this.form.controls.address.enabled) {
      return;
    }

    this.saveAddress(this.form.value as AddressData);
  }

  private extractEntrance(geocodeResult: GeocodeResult): any {
    const address: any = geocodeResult.properties.get('metaDataProperty.GeocoderMetaData.Address', '');

    return address?.Components?.find((item: any) => item.kind === 'entrance')?.name?.replace(/\D/g, '');
  }

  private saveAddress(data: AddressData): void {
    this.deliveryAddressService.editableAddressId$.pipe(take(1)).subscribe((addressId) => {
      const checkoutAddress: Address = mapFormDataToAddress(data, addressId);

      if (addressId) {
        this.deliveryAddressService.editSavedAddress(checkoutAddress);
      } else {
        this.deliveryAddressService.saveAddress(checkoutAddress);
      }
      this.deliveryAddressService.closeAddressModal();
    });
  }

  private getExistingAddressData(addressId: string): Observable<Nullable<AddressData>> {
    return this.deliveryAddressService.getAddress(addressId).pipe(
      filter<Address>(Boolean),
      map((address) => mapAddressToFormData(address)),
    );
  }

  private applyConditionalValidators(): void {
    this.toggleApartmentValidators(this.form.controls.privateHouse.value === true);
    this.toggleTransitLimitValidators(this.form.controls.hasLimit.value === true);
  }

  private toggleApartmentValidators(isPrivateHouse: boolean): void {
    const { appartment, floor } = this.form.controls;

    if (isPrivateHouse) {
      appartment.clearValidators();
      floor.clearValidators();
    } else {
      appartment.setValidators(Validators.required);
      floor.setValidators(Validators.required);
    }

    appartment.updateValueAndValidity({ emitEvent: false });
    floor.updateValueAndValidity({ emitEvent: false });
  }

  private toggleTransitLimitValidators(hasLimit: boolean): void {
    const { heightLimit, widthLimit } = this.form.controls;

    if (hasLimit) {
      heightLimit.setValidators(this.atLeastOneTransitLimitValidator.bind(this));
      widthLimit.setValidators(this.atLeastOneTransitLimitValidator.bind(this));
    } else {
      heightLimit.clearValidators();
      widthLimit.clearValidators();
    }

    heightLimit.updateValueAndValidity({ emitEvent: false });
    widthLimit.updateValueAndValidity({ emitEvent: false });
  }

  private atLeastOneTransitLimitValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;

    if (!parent) {
      return null;
    }

    const heightValue = parent.get('heightLimit')?.value;
    const widthValue = parent.get('widthLimit')?.value;

    if (this.isEmptyValue(heightValue) && this.isEmptyValue(widthValue)) {
      return { transitLimitRequired: true };
    }

    return null;
  }

  private isEmptyValue(value: unknown): boolean {
    return value === null || value === undefined || value === '';
  }

  private addressValidator(control: AbstractControl): ValidationErrors | null {
    const geoObject = this.componentService.geoObject$.value;

    if (
      !control.value ||
      !geoObject ||
      geoObject.geocodeResult.getAddressLine() !== control.value ||
      (!geoObject.exactPoint && !geoObject.geocodeResult.getPremiseNumber())
    ) {
      return { required: true };
    }

    return null;
  }

  private setAddressValue(address: string): void {
    this.form.controls.address.setValue(address);
  }

  private resetApartmentData(): void {
    this.form.controls.appartment.reset();
    this.form.controls.entrance.reset();
    this.form.controls.floor.reset();
    this.form.controls.domofon.reset();
    this.form.controls.passengerLift.reset();
    this.form.controls.cargoLift.reset();
  }

  private resetTransitLimits(): void {
    this.form.controls.heightLimit.reset();
    this.form.controls.widthLimit.reset();
  }

  private getEmptyAddressData(): AddressData {
    return {
      address: null,
      appartment: null,
      entrance: null,
      floor: null,
      heightLimit: null,
      widthLimit: null,
      hasLimit: false,
      domofon: null,
      privateHouse: false,
      cargoLift: false,
      passengerLift: false,
      carryingDistance: null,
      addressAdditionalData: null,
    };
  }
}
